import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import ConstructionIcon from "@mui/icons-material/Construction";
import LockIcon from "@mui/icons-material/Lock";
import { getLocalStorage, setLocalStorage, removeLocalStorage } from "../../utils/storage";

const LOCKOUT_DURATION = 30 * 1000; // 30 seconds
const MAX_ATTEMPTS = 3;

const InDevelopmentPage = () => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLocked, setIsLocked] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    // Check for existing lockout on mount
    const checkLockout = () => {
      const storedLockout = getLocalStorage("dev_page_lockout");
      if (storedLockout) {
        const lockoutTime = parseInt(storedLockout, 10);
        const now = Date.now();
        if (now < lockoutTime) {
          setIsLocked(true);
          setTimeLeft(Math.ceil((lockoutTime - now) / 1000));
        } else {
          // Lockout expired
          removeLocalStorage("dev_page_lockout");
          removeLocalStorage("dev_page_attempts");
          setIsLocked(false);
        }
      }
    };

    checkLockout();

    // Timer for countdown
    const interval = setInterval(() => {
      if (isLocked) {
        checkLockout();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isLocked]);

  const handlePasswordSubmit = (e?: React.FormEvent) => {
    e?.preventDefault(); // Prevent form submission if strictly needed, though button onClick handles it

    if (isLocked) return;

    if (password === "flamingo") {
      setLocalStorage("allow_access", "true");
      // Clear any lockout data on success
      removeLocalStorage("dev_page_lockout");
      removeLocalStorage("dev_page_attempts");
      window.location.reload();
    } else {
      // Handle failure
      const attempts = parseInt(getLocalStorage("dev_page_attempts") || "0", 10) + 1;
      setLocalStorage("dev_page_attempts", attempts.toString());

      if (attempts >= MAX_ATTEMPTS) {
        const lockoutTime = Date.now() + LOCKOUT_DURATION;
        setLocalStorage("dev_page_lockout", lockoutTime.toString());
        setIsLocked(true);
        setErrorMessage(`Too many incorrect attempts. Locked out for ${LOCKOUT_DURATION / 1000} seconds.`);
        setError(true);
      } else {
        setErrorMessage("Incorrect password");
        setError(true);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setError(false);
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 8 }}>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 50, damping: 10 }}
      >
        <ConstructionIcon
          sx={{ fontSize: 100, color: "primary.main", mb: 2 }}
        />
      </motion.div>
      <Typography variant="h3" gutterBottom>
        Page Under Construction
      </Typography>
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <Typography variant="body1">
          We are working hard to bring this page to life. Please check back
          soon!
        </Typography>
      </motion.div>
      <motion.div
        initial={{ x: "-100vw" }}
        animate={{ x: 0 }}
        transition={{ type: "spring", stiffness: 100, delay: 0.5 }}
      >
        <Box
          sx={{
            mt: 5,
            mb: 5,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          <ConstructionIcon sx={{ fontSize: 50, color: "primary.main" }} />
          <ConstructionIcon sx={{ fontSize: 50, color: "primary.main" }} />
          <ConstructionIcon sx={{ fontSize: 50, color: "primary.main" }} />
        </Box>
      </motion.div>

      <Box
        sx={{
          mt: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Have an access code?
        </Typography>
        <Box
          component="form"
          onSubmit={handlePasswordSubmit}
          sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
        >
          <TextField
             type="password"
             label={isLocked ? `Locked (${timeLeft}s)` : "Enter password"}
             variant="outlined"
             size="small"
             value={password}
             onChange={(e) => setPassword(e.target.value)}
             disabled={isLocked}
             error={isLocked}
          />
          <Button
            variant="contained"
            onClick={(e) => handlePasswordSubmit(e)}
            disabled={isLocked || !password}
            startIcon={isLocked ? <LockIcon /> : null}
          >
            {isLocked ? "Locked" : "Enter"}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default InDevelopmentPage;
